<script setup lang="ts">
import UECarousel from '@components/base/UECarousel.vue'
import { computed } from 'vue'

interface Props {
  editable?: boolean
  grayScale?: boolean
  logos: string[]
  showControls?: boolean
}

const DESKTOP_LOGOS_MAX = 4

const props = withDefaults(defineProps<Props>(), {
  editable: false,
  grayScale: false,
  logos: () => [],
  showControls: true,
})

const slidesToShow = computed(() => {
  return {
    desktop: Math.min(DESKTOP_LOGOS_MAX, props.logos.length),
    mobile: 1,
  }
})

const logoClass = computed(() => {
  return props.grayScale ? 'grayscale' : ''
})

const padding = computed(() => {
  // If we're in editor mode we need more space on top for the toolbar
  return props.editable ? '3rem 0 0 0' : '0'
})

const emit = defineEmits<{
  (e: 'logoRemoved', logo: string, index: number): void
}>()

const removeLogo = (index: number): void => {
  emit('logoRemoved', props.logos[index], index)
}
</script>

<template>
  <div class="logo-carousel">
    <UECarousel
      :items="logos"
      :slides-to-show="slidesToShow"
      :loop="true"
      :auto-height="true"
      :auto-play="false"
      :show-controls="showControls"
      :padding="padding"
      gap="1rem"
      slide-class="d-flex justify-content-center align-items-center logo-slide-container"
      direction="horizontal"
    >
      <template #slide="{ item, index }">
        <div class="logo-slide d-flex justify-content-center position-relative">
          <div class="image-container position-relative">
            <img :class="logoClass" :src="item" />
          </div>
          <ul
            v-if="editable"
            class="logo-toolbar rounded-sm d-flex align-items-center justify-content-end px-2 m-0"
          >
            <li class="fs-sm px-2" @click="removeLogo(index)">
              <i class="bi bi-trash mr-1"></i>
              Delete
            </li>
          </ul>
        </div>
      </template>
    </UECarousel>
  </div>
</template>

<style lang="sass">
.logo-carousel
  overflow: visible

.logo-slide-container
  overflow: visible

.image-container
  width: 50%

  img
    max-height: 100px
    width: 100%

  // Desktop responsive sizing
  @media (min-width: 992px)
    width: 150px
    min-width: 150px

.logo-slide
  .grayscale
    filter: grayscale(1)

  &:hover .logo-toolbar
    opacity: 1

.logo-toolbar
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2)
  gap: 0.5rem
  height: 2rem
  opacity: 0
  position: absolute
  top: -3rem
  transition: opacity 0.2s ease
  white-space: nowrap
  width: max-content

  li
    list-style: none
    cursor: pointer
    padding-top: 1px
    padding-bottom: 1px
</style>
